import React, { useEffect, useState } from 'react';
import { X, Paperclip, Eye, Link2, Edit2, ExternalLink } from 'lucide-react';
import { expenseAPI, paymentProofAPI } from '../../services/api';
import useDialog from '../../hooks/useDialog';
import { FileHoverPreview } from '../MediaPreview';
import { authedMediaUrl } from '../../utils/media';
import {
  formatCurrency, formatDate, formatSignedAmount, paymentLabel, referenceOf, TypeBadge, LoadingRow,
} from './shared';

// Everything known about one entry: the record, its files and screenshots, who entered it, and the
// entry it is paired with (the withdrawal recorded with an expense, or the expense a withdrawal
// was recorded with).

const Field = ({ label, children, className = '' }) => (
  <div className={className}>
    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{label}</p>
    <div className="mt-0.5 text-sm text-gray-900">{children}</div>
  </div>
);

const FileRow = ({ url, name, isPdf }) => (
  <div className="flex items-center gap-3 px-3 py-2 bg-white">
    <Paperclip className="w-4 h-4 text-gray-400 flex-shrink-0" />
    <FileHoverPreview url={url} label={name} isPdf={isPdf} className="flex-1 min-w-0 inline-flex">
      {({ href }) => (
        <a href={href} target="_blank" rel="noreferrer" className="min-w-0 inline-flex items-center gap-1.5" title={`${name}: opens in a new tab`}>
          <Eye className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
          <span className="truncate text-sm text-emerald-700 hover:underline">{name}</span>
        </a>
      )}
    </FileHoverPreview>
    <a href={authedMediaUrl(url)} target="_blank" rel="noreferrer" className="p-1.5 text-gray-400 hover:text-emerald-700 rounded-lg" title="Open in a new tab">
      <ExternalLink className="w-4 h-4" />
    </a>
  </div>
);

const PairedCard = ({ title, entry }) => (
  <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3">
    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1.5">
      <Link2 className="w-3.5 h-3.5" /> {title}
    </p>
    <div className="mt-1 flex items-center justify-between gap-3">
      <div className="min-w-0">
        <p className="text-sm font-medium text-gray-900 truncate">{entry.description}</p>
        <p className="text-xs text-gray-500">{formatDate(entry.entryDate)} · {entry.categoryPath || '-'}</p>
      </div>
      <span className={`text-sm font-semibold whitespace-nowrap ${entry.entryType === 'WITHDRAWAL' ? 'text-blue-700' : 'text-red-700'}`}>
        {formatSignedAmount(entry)}
      </span>
    </div>
  </div>
);

const EntryDetailModal = ({ entryId, canEdit, onClose, onEdit }) => {
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState(null);
  const dialogRef = useDialog(onClose, { confirmDiscard: false });

  useEffect(() => {
    let alive = true;
    expenseAPI.getEntry(entryId)
      .then(d => { if (alive) setDetail(d); })
      .catch(err => { if (alive) setError(err.message || 'Could not load the entry'); });
    return () => { alive = false; };
  }, [entryId]);

  const isWithdrawal = detail?.entryType === 'WITHDRAWAL';

  return (
    <div ref={dialogRef} className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden" role="dialog" aria-modal="true" aria-labelledby="entry-detail-title">
        <div className="flex-shrink-0 px-6 py-4 bg-gradient-to-r from-navy-800 to-navy-700 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <h2 id="entry-detail-title" className="text-lg font-semibold text-white truncate">{detail?.description || 'Entry'}</h2>
            {detail && (
              <p className="text-xs text-navy-100 mt-0.5">{formatDate(detail.entryDate)} · {detail.categoryPath || 'No department'}</p>
            )}
          </div>
          <button aria-label="Close" onClick={onClose} className="p-2 rounded-lg hover:bg-white/10 transition-colors flex-shrink-0">
            <X className="h-5 w-5 text-white" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {error ? (
            <p className="text-sm text-red-600">{error}</p>
          ) : !detail ? (
            <LoadingRow />
          ) : (
            <>
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <TypeBadge type={detail.entryType} />
                <span className={`text-2xl font-bold ${isWithdrawal ? 'text-blue-700' : 'text-red-700'}`}>{formatSignedAmount(detail)}</span>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <Field label="Date">{formatDate(detail.entryDate)}</Field>
                <Field label="Department">{detail.categoryPath || '-'}</Field>
                <Field label="Paid by">
                  {paymentLabel(detail.paymentMode)}
                  {referenceOf(detail) && <span className="block font-mono text-xs text-gray-600">{referenceOf(detail)}{detail.chequeBank ? ` · ${detail.chequeBank}` : ''}</span>}
                  {!referenceOf(detail) && detail.chequeBank && <span className="block text-xs text-gray-600">{detail.chequeBank}</span>}
                </Field>
                <Field label="Amount">{formatCurrency(detail.amount)}</Field>
                <Field label="Entered by">
                  {detail.createdByName || '-'}
                  {detail.createdAt && <span className="block text-xs text-gray-500">{formatDate(detail.createdAt)}</span>}
                </Field>
                {detail.legacyId && <Field label="Reporting app id">#{detail.legacyId}</Field>}
              </div>

              {detail.notes && (
                <Field label="Notes"><p className="whitespace-pre-wrap">{detail.notes}</p></Field>
              )}

              {detail.linkedWithdrawal && <PairedCard title="Withdrawal recorded with this expense" entry={detail.linkedWithdrawal} />}
              {detail.linkedExpense && <PairedCard title="Recorded with this expense" entry={detail.linkedExpense} />}

              {(detail.attachments || []).length > 0 && (
                <Field label="Files">
                  <div className="rounded-xl border border-gray-200 divide-y divide-gray-100 overflow-hidden mt-1">
                    {detail.attachments.map(a => (
                      <FileRow key={a.id} url={expenseAPI.attachmentFileUrl(a.id)} name={a.originalFileName || 'Attachment'} isPdf={(a.contentType || '').includes('pdf')} />
                    ))}
                  </div>
                </Field>
              )}

              {(detail.paymentProofs || []).length > 0 && (
                <Field label="Payment screenshots">
                  <div className="rounded-xl border border-gray-200 divide-y divide-gray-100 overflow-hidden mt-1">
                    {detail.paymentProofs.map(p => (
                      <FileRow key={p.id} url={paymentProofAPI.fileUrl(p.id)} name={p.originalFileName || 'Screenshot'} isPdf={(p.contentType || '').includes('pdf')} />
                    ))}
                  </div>
                </Field>
              )}

              {(detail.receiptLines || []).length > 1 && (
                <Field label="Other lines on the same bill">
                  <ul className="mt-1 divide-y divide-gray-100 rounded-xl border border-gray-200 overflow-hidden">
                    {detail.receiptLines.filter(l => l.id !== detail.id).map(l => (
                      <li key={l.id} className="flex items-center justify-between px-3 py-2 text-sm">
                        <span className="truncate">{l.description}</span>
                        <span className="font-semibold whitespace-nowrap">{formatCurrency(l.amount)}</span>
                      </li>
                    ))}
                  </ul>
                </Field>
              )}
            </>
          )}
        </div>

        <div className="flex-shrink-0 px-6 py-4 border-t border-gray-100 bg-gray-50 flex items-center justify-end gap-3">
          <button type="button" onClick={onClose} className="px-5 py-2.5 text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors font-medium">
            Close
          </button>
          {canEdit && detail && (
            <button type="button" onClick={() => onEdit(detail)} className="px-5 py-2.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors font-medium flex items-center gap-2">
              <Edit2 className="h-4 w-4" /> Edit
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default EntryDetailModal;
